import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, systemAPI, settingsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineUser } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

function Register() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await systemAPI.getVersion();
        setVersion(response.data.version);
      } catch (error) {
        console.error('Error fetching version:', error);
        setVersion('1.4.1'); // Fallback
      }
    };

    const fetchRegistrationStatus = async () => {
      try {
        const response = await settingsAPI.getRegistrationStatus();
        setRegistrationEnabled(response.data.enabled);
      } catch (error) {
        console.error('Error fetching registration status:', error);
        setRegistrationEnabled(true); // Default to enabled on error
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchVersion();
    fetchRegistrationStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!username || !email || !password || !confirmPassword) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }

    setLoading(true);
    try {
      // Get current language (detected from browser or user preference)
      const currentLanguage = i18next.language || 'pt-BR';

      const response = await authAPI.register(username, email, password, currentLanguage);

      toast.success(t('auth.registerSuccess'));

      // Redirect to email verification page
      navigate('/verify-email', { state: { email } });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);

      // Clear password fields on error
      setPassword('');
      setConfirmPassword('');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!registrationEnabled) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-logo">
            <h1>My Series</h1>
            <p>{t('auth.registrationDisabled')}</p>
          </div>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Link
              to="/login"
              className="btn btn-primary"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              {t('auth.backToLogin')}
            </Link>
          </div>

          <p
            style={{
              textAlign: 'center',
              marginTop: '24px',
              fontSize: '12px',
              color: '#6b7280',
            }}
          >
            {t('auth.copyright')}
          </p>
          <p
            style={{
              textAlign: 'center',
              marginTop: '8px',
              fontSize: '11px',
              color: '#9ca3af',
            }}
          >
            {t('common.version')} {version}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <h1>My Series</h1>
          <p>{t('auth.registerSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('common.username')}</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineUser
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  width: '20px',
                  height: '20px',
                }}
              />
              <input
                type="text"
                className="form-input"
                style={{ paddingLeft: '48px' }}
                placeholder={t('auth.enterUsername')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('common.email')}</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineMail
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  width: '20px',
                  height: '20px',
                }}
              />
              <input
                type="email"
                className="form-input"
                style={{ paddingLeft: '48px' }}
                placeholder={t('profile.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('common.password')}</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineLockClosed
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  width: '20px',
                  height: '20px',
                }}
              />
              <input
                type="password"
                className="form-input"
                style={{ paddingLeft: '48px' }}
                placeholder={t('common.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('auth.confirmNewPasswordLabel')}</label>
            <div style={{ position: 'relative' }}>
              <HiOutlineLockClosed
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  width: '20px',
                  height: '20px',
                }}
              />
              <input
                type="password"
                className="form-input"
                style={{ paddingLeft: '48px' }}
                placeholder={t('auth.confirmNewPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%', marginTop: '8px' }}
            disabled={loading}
          >
            {loading ? t('auth.registering') : t('auth.register')}
          </button>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>
              {t('auth.alreadyHaveAccount')}{' '}
            </span>
            <Link
              to="/login"
              style={{
                color: 'var(--primary)',
                fontSize: '14px',
                textDecoration: 'underline',
              }}
            >
              {t('auth.login')}
            </Link>
          </div>
        </form>

        <p
          style={{
            textAlign: 'center',
            marginTop: '24px',
            fontSize: '12px',
            color: '#6b7280',
          }}
        >
          {t('auth.copyright')}
        </p>
        <p
          style={{
            textAlign: 'center',
            marginTop: '8px',
            fontSize: '11px',
            color: '#9ca3af',
          }}
        >
          {t('common.version')} {version}
        </p>
      </div>
    </div>
  );
}

export default Register;
